import type { Form } from '../../../services/formService';
import { isMechanicDailyLog } from './mechanicDailyLog';

export type SubmissionFormKind = 'dailylog_rb' | 'dailylog_mechanic' | 'hired_equipment';

export function getSubmissionFormKind(form: Form): SubmissionFormKind {
  const hired = form.fields.some(
    (field) =>
      field.path.includes('EquipmentTimeWorkedJ') ||
      field.field === 'AmountBilled' ||
      field.path.includes('SubcontractorNameCom')
  );
  if (hired) return 'hired_equipment';
  if (isMechanicDailyLog(form)) return 'dailylog_mechanic';
  return 'dailylog_rb';
}

/** Read access: one per form family (RB daily log, Mechanic daily log, Hired Equipment). */
export const SUBMISSION_READ_KEYS: Record<SubmissionFormKind, string> = {
  dailylog_rb: 'submission:dailylog_rb_read',
  dailylog_mechanic: 'submission:dailylog_mechanic_read',
  hired_equipment: 'submission:hired_equipment_read'
};

export const SUBMISSION_APPROVE_KEYS: Record<SubmissionFormKind, string> = {
  dailylog_rb: 'submission:dailylog_rb_approve',
  dailylog_mechanic: 'submission:dailylog_mechanic_approve',
  hired_equipment: 'submission:hired_equipment_approve'
};

/**
 * Lets the holder's own hired equipment submission go in approved rather than pending. Mirrors
 * PERMISSION_HE_AUTO_APPROVE on the server.
 */
export const HE_AUTO_APPROVE_PERMISSION = 'submission:hired_equipment_auto_approve';

/**
 * Extra keys that also authorise approving a kind, beyond the canonical one above. Mirrors
 * SUBMISSION_EXTRA_APPROVE_KEYS on the server, and must stay in step with it.
 */
export const SUBMISSION_EXTRA_APPROVE_KEYS: Partial<Record<SubmissionFormKind, string[]>> = {
  hired_equipment: [HE_AUTO_APPROVE_PERMISSION]
};

export const SUBMISSION_WITHDRAW_APPROVAL_KEYS: Record<SubmissionFormKind, string> = {
  dailylog_rb: 'submission:dailylog_rb_withdraw_approval',
  dailylog_mechanic: 'submission:dailylog_mechanic_withdraw_approval',
  hired_equipment: 'submission:hired_equipment_withdraw_approval'
};

export const SUBMISSION_WITHDRAW_REVISIONS_KEYS: Record<SubmissionFormKind, string> = {
  dailylog_rb: 'submission:dailylog_rb_withdraw_revisions',
  dailylog_mechanic: 'submission:dailylog_mechanic_withdraw_revisions',
  hired_equipment: 'submission:hired_equipment_withdraw_revisions'
};

export const SUBMISSION_DECLINE_KEYS: Record<SubmissionFormKind, string> = {
  dailylog_rb: 'submission:dailylog_rb_decline',
  dailylog_mechanic: 'submission:dailylog_mechanic_decline',
  hired_equipment: 'submission:hired_equipment_decline'
};

export const SUBMISSION_WITHDRAW_DECLINE_KEYS: Record<SubmissionFormKind, string> = {
  dailylog_rb: 'submission:dailylog_rb_withdraw_decline',
  dailylog_mechanic: 'submission:dailylog_mechanic_withdraw_decline',
  hired_equipment: 'submission:hired_equipment_withdraw_decline'
};

/** Send for revisions — Daily Log (RB / Mechanic) and Hired Equipment. */
export const SUBMISSION_REVISION_KEYS: Record<SubmissionFormKind, string> = {
  dailylog_rb: 'submission:dailylog_rb_revision',
  dailylog_mechanic: 'submission:dailylog_mechanic_revision',
  hired_equipment: 'submission:he_revisions'
};

/** @deprecated Use SUBMISSION_REVISION_KEYS — kept for existing Daily Log call sites. */
export const SUBMISSION_DAILYLOG_REVISION_KEYS: Record<
  'dailylog_rb' | 'dailylog_mechanic',
  string
> = {
  dailylog_rb: SUBMISSION_REVISION_KEYS.dailylog_rb,
  dailylog_mechanic: SUBMISSION_REVISION_KEYS.dailylog_mechanic
};

export const SUBMISSION_DAILYLOG_EDIT_KEYS: Record<'dailylog_rb' | 'dailylog_mechanic', string> = {
  dailylog_rb: 'submission:dailylog_rb_edit',
  dailylog_mechanic: 'submission:dailylog_mechanic_edit'
};

export const SUBMISSION_DAILYLOG_MANAGE_KEYS: Record<'dailylog_rb' | 'dailylog_mechanic', string> = {
  dailylog_rb: 'submission:dailylog_rb_manage',
  dailylog_mechanic: 'submission:dailylog_mechanic_manage'
};

export function canAccessSubmissionsPage(has: (key: string) => boolean): boolean {
  return (
    has(SUBMISSION_READ_KEYS.dailylog_rb) ||
    has(SUBMISSION_READ_KEYS.dailylog_mechanic) ||
    has(SUBMISSION_READ_KEYS.hired_equipment)
  );
}

export function canApproveSubmissionForForm(has: (key: string) => boolean, form: Form): boolean {
  const kind = getSubmissionFormKind(form);
  return [SUBMISSION_APPROVE_KEYS[kind], ...(SUBMISSION_EXTRA_APPROVE_KEYS[kind] ?? [])].some(has);
}

export function canSendForRevisions(has: (key: string) => boolean, form: Form): boolean {
  const kind = getSubmissionFormKind(form);
  return has(SUBMISSION_REVISION_KEYS[kind]);
}

/** @deprecated Use canSendForRevisions — HE was previously hard-hidden. */
export function canSendDailyLogForRevisions(has: (key: string) => boolean, form: Form): boolean {
  return canSendForRevisions(has, form);
}

export function canWithdrawApprovalForForm(has: (key: string) => boolean, form: Form): boolean {
  const kind = getSubmissionFormKind(form);
  return has(SUBMISSION_WITHDRAW_APPROVAL_KEYS[kind]);
}

export function canWithdrawRevisionsForForm(has: (key: string) => boolean, form: Form): boolean {
  const kind = getSubmissionFormKind(form);
  return has(SUBMISSION_WITHDRAW_REVISIONS_KEYS[kind]);
}

export function canDeclineSubmissionForForm(has: (key: string) => boolean, form: Form): boolean {
  const kind = getSubmissionFormKind(form);
  return has(SUBMISSION_DECLINE_KEYS[kind]);
}

export function canWithdrawDeclineForForm(has: (key: string) => boolean, form: Form): boolean {
  const kind = getSubmissionFormKind(form);
  return has(SUBMISSION_WITHDRAW_DECLINE_KEYS[kind]);
}

export function canEditDailyLogForForm(has: (key: string) => boolean, form: Form): boolean {
  const kind = getSubmissionFormKind(form);
  if (kind === 'hired_equipment') {
    return has('submission:he_manage');
  }
  return has(SUBMISSION_DAILYLOG_EDIT_KEYS[kind]);
}

export function canManageDailyLogForForm(has: (key: string) => boolean, form: Form): boolean {
  const kind = getSubmissionFormKind(form);
  if (kind === 'hired_equipment') {
    return has('submission:he_manage');
  }
  return has(SUBMISSION_DAILYLOG_MANAGE_KEYS[kind]);
}
